import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SessionManagement = ({ client, onSessionTransaction }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [activeTab, setActiveTab] = useState('add');
  const [description, setDescription] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const sessions = client?.sessions || 0;

  const quickAmounts = {
    add: [1, 5, 10],
    subtract: [1, 5]
  };

  const handleQuickAction = async (type, amount) => {
    const desc =
      type === 'add'
        ? `Quick added ${amount} sessions`
        : `Quick subtracted ${amount} sessions`;
    await handleTransaction(type, amount, desc);
  };

  const handleCustomTransaction = async () => {
    const amount = parseInt(customAmount, 10);
    if (!amount || amount <= 0) {
      setFeedback({ type: 'error', message: 'Please enter a valid number of sessions greater than 0' });
      return;
    }

    if (activeTab === 'subtract' && amount > sessions) {
      setFeedback({
        type: 'error',
        message: `Insufficient sessions. Available: ${sessions}`
      });
      return;
    }


    await handleTransaction(activeTab, amount, description || `Custom ${activeTab === 'add' ? 'add' : 'subtract'} - ${amount} sessions`);
    setCustomAmount('');
    setDescription('');
  };

  const handleTransaction = async (type, amount, desc) => {
    if (!onSessionTransaction) return;

    setIsProcessing(true);
    setFeedback({ type: '', message: '' });

    try {
      const result = await onSessionTransaction(type, amount, desc);

      if (result?.success) {
        setFeedback({
          type: 'success',
          message: `Successfully ${type === 'add' ? 'added' : 'subtracted'} ${amount} sessions`
        });
      } else {
        setFeedback({
          type: 'error',
          message: result?.error || 'Session transaction failed. Please try again.'
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'An unexpected error occurred during the session transaction'
      });
    } finally {
      setIsProcessing(false);

      // Clear feedback after 4 seconds
      setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 4000);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-muted/20 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
          <Icon name="CalendarClock" size={20} />
          <span>Session Management</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Add purchased sessions or subtract used sessions from the client&apos;s account.
        </p>
      </div>

      <div className="p-6">
        {/* Current Sessions Display */}
        <div className="bg-muted/40 rounded-lg p-6 mb-6 border border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Current Sessions</h3>
              <div className="text-3xl font-bold font-mono text-foreground">
                {sessions}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Sessions are tracked separately from minutes. Use this section for visit-based packages.
              </p>
            </div>
            <Icon name="Users" size={40} className="text-muted-foreground" />
          </div>
        </div>

        {/* Feedback Message */}
        {feedback.message && (
          <div
            className={`mb-6 p-4 rounded-lg border flex items-start space-x-3 ${
              feedback.type === 'success'
                ? 'bg-success/10 border-success/20 text-success'
                : 'bg-destructive/10 border-destructive/20 text-destructive'
            }`}
          >
            <Icon
              name={feedback.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
              size={18}
              className="mt-0.5 flex-shrink-0"
            />
            <div>
              <span className="text-sm font-medium block">{feedback.message}</span>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex rounded-lg bg-muted p-1 mb-6">
          <button
            onClick={() => setActiveTab('add')}
            className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
              activeTab === 'add' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Plus" size={16} />
            <span>Add Sessions</span>
          </button>
          <button
            onClick={() => setActiveTab('subtract')}
            className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
              activeTab === 'subtract'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Minus" size={16} />
            <span>Subtract Sessions</span>
          </button>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h4 className="text-sm font-medium text-foreground mb-4 flex items-center space-x-2">
            <Icon name="Zap" size={16} />
            <span>Quick Actions</span>
          </h4>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {quickAmounts[activeTab].map((amount) => (
              <Button
                key={amount}
                variant="outline"
                onClick={() => handleQuickAction(activeTab, amount)}
                loading={isProcessing}
                disabled={activeTab === 'subtract' && amount > sessions}
                className={`${
                  activeTab === 'add'
                    ? 'hover:bg-success hover:text-success-foreground hover:border-success'
                    : 'hover:bg-warning hover:text-warning-foreground hover:border-warning'
                } transition-all duration-200 h-12`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <span className="text-lg font-bold">
                    {activeTab === 'add' ? '+' : '-'}
                    {amount}
                  </span>
                  <span className="text-xs">sessions</span>
                </div>
              </Button>
            ))}
          </div>
        </div>

        {/* Custom Amount */}
        <div className="space-y-6">
          <h4 className="text-sm font-medium text-foreground flex items-center space-x-2">
            <Icon name="Settings" size={16} />
            <span>Custom Sessions</span>
          </h4>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label htmlFor="customSessions" className="block text-sm font-medium text-muted-foreground mb-2">
                Sessions <span className="text-destructive">*</span>
              </label>
              <Input
                id="customSessions"
                type="number"
                placeholder="Enter number of sessions"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                min="1"
                max={activeTab === 'subtract' ? sessions : 999}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {activeTab === 'subtract'
                  ? `Maximum: ${sessions} sessions`
                  : 'Enter any positive number of sessions'}
              </p>
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-muted-foreground mb-2">
                Description (Optional)
              </label>
              <Input
                id="description"
                type="text"
                placeholder="Transaction description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={100}
              />
            </div>
          </div>

          <Button
            onClick={handleCustomTransaction}
            loading={isProcessing}
            disabled={
              !customAmount ||
              parseInt(customAmount, 10) <= 0 ||
              (activeTab === 'subtract' && parseInt(customAmount, 10) > sessions)
            }
            className={`w-full lg:w-auto px-8 py-3 ${
              activeTab === 'add'
                ? 'bg-success hover:bg-success/90 text-success-foreground'
                : 'bg-warning hover:bg-warning/90 text-warning-foreground'
            }`}
            iconName={activeTab === 'add' ? 'Plus' : 'Minus'}
            iconPosition="left"
          >
            {activeTab === 'add' ? 'Add' : 'Subtract'} {customAmount || '0'} Sessions
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;


